import { GetServerSidePropsContext } from 'next/types';
import React from 'react';
import { ssrRedirectToErrorPage } from 'src/config/ssrRedirectToErrorPage';
import { getShipPageData } from 'src/helpers/ship-page/getShipPageData';
import { useHandleSearchSubmit } from 'src/hooks/useHandleSearchSubmit';
import { ILandingPageProps } from '../../../interfaces/IPages';
import { Breadcrumbs } from '../../../src/components/atoms/breadcrumbs/breadcrumbs';
import { StyledBreadcrumbsContainer } from '../../../src/components/atoms/breadcrumbs/breadcrumbs.style';
import { SubpageStickyBar } from '../../../src/components/molecules/subpage-sticky-bar/subpage-sticky-bar';
import { HeadTags } from '../../../src/components/sections/head-tags/head-tags';
import { getBreadcrumbsPath } from '../../../src/components/sections/landing-breadcrumbs/landing-breadcrumbs';
import { UGC } from '../../../src/components/sections/ugc/ugc';
import { getUGCPropsFromData } from '../../../src/components/sections/ugc/ugc.data';
import { LocaleConfigContext } from '../../../src/contexts/locale-config/locale-config';
import { getSsrRedirectTo } from '../../../src/helpers/getSSRRedirectTo';

const ReviewsPage = (props: ILandingPageProps) => {
    const localeConfig = React.useContext(LocaleConfigContext);

    const modulesData = props?.pageProps?.modulesData;
    const landingPage = props?.pageProps?.getLandingPage;
    const meta = props?.pageProps?.getLandingPage?.meta;
    const slug = props?.pageProps?.slug;

    const breadcrumbsPath = getBreadcrumbsPath(landingPage, modulesData, localeConfig);

    const hostUrl = props?.hostUrl;

    const shipPageUrl = `/${localeConfig.slugs.ship.prefix}/${landingPage.slug}`;

    const headTagsData = {
        ...meta,
        hostUrl,
        landingPage,
        modulesData,
        pageType: 'landingPage',
    };

    const subPageObserverRef = React.useRef(null);

    const handleSubmit = useHandleSearchSubmit({
        id: landingPage.id,
        type: landingPage.type,
        title: landingPage.title,
    });

    const ugcProps = getUGCPropsFromData({
        modulesData,
        getLandingPage: landingPage,
        slug,
        ugcSlug: localeConfig.slugs.ugc,
        subpage: landingPage.subpage,
    });

    if (!ugcProps) {
        return null;
    }

    return (
        <>
            <HeadTags {...headTagsData} />
            <div ref={subPageObserverRef}>
                <SubpageStickyBar
                    observerRef={subPageObserverRef}
                    shipUrl={shipPageUrl}
                    onSearchSubmit={handleSubmit}
                />
            </div>

            <UGC {...ugcProps} />

            <StyledBreadcrumbsContainer>
                <Breadcrumbs paths={breadcrumbsPath} />
            </StyledBreadcrumbsContainer>
        </>
    );
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
    const [, pageType, slug, slugSubpage] = context.resolvedUrl.split('/');

    const pageData = await getShipPageData({ slug, pageType, slugSubpage, ugcLimit: 10 });

    if (!pageData) {
        return ssrRedirectToErrorPage;
    }

    const { landingPage, modulesData, configData } = pageData;

    const ugcData = modulesData['ugcData'];
    const hasReviews = Boolean(ugcData?.aggregation?.data?.attributes?.numRatings);

    if (!ugcData || !hasReviews || !ugcData.rating) {
        return getSsrRedirectTo(landingPage.url);
    }

    return {
        props: {
            getLandingPage: landingPage,
            modulesData: modulesData,
            slug,
            globalConfigData: configData, // Used in the app as globalConfigData
        },
    };
}

export default ReviewsPage;
